import React, { useEffect, useState } from "react";
import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import DeckTable from "../../components/tables/DeckTable";
import PageMeta from "../../components/common/PageMeta";
import DeckService from "../../services/decks.service";
import Input from "../../components/form/input/InputField";
import { Search } from "lucide-react";
import { useParams } from "react-router";
import FlashcardService from "@/services/flashcard.service";
import { isValidURL } from "@/utils/validation";

interface user {
    _id: string;
    full_name: string;
    email_address: string;
    phone_number: string;
    role: string;
    status: string;
    createdAt: string;
}

interface category {
    _id: string;
    title: string;
}

export interface Deck {
    _id: string;
    title: string;
    description: string;
    type: string;
    likes: number;
    completion_count: number;
    average_time_spent: number;
    view_count: number;
    active_usage_count: number;
    retention_rate: number;
    search_appearances: number;
    click_count: number;
    conversion_rate: number;
    createdAt: string;
    user: user[];
    flashcard_count: number;
    category: category[];
    image: string;
}

export default function ListDeckInCategory() {
    const { category_id } = useParams();
    const [decks, setDecks] = useState<Deck[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const fetchUsers = async (page: number, search?: string) => {
        try {
            setLoading(true);
            setError("");
            const response = await DeckService.getDecksInCategory(category_id, {
                page: page,
                limit: 10,
                search: search
            });
            let updatedDecks = response.data.deck || [];
            for (const it of updatedDecks) {
                if (it.image) {
                    const image = isValidURL(it.image) ? it.image : await fetchSignedUrl(it.image);
                    it.image = image;
                } else {
                    it.image = "";
                }
            }
            setDecks(updatedDecks);
            setTotalPages(Math.ceil((response.data.count || 0) / 10));
            setCurrentPage(page);
        } catch (err) {
            setError("Failed to fetch decks. Please try again later.");
            console.error("Error fetching decks:", err);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    };

    useEffect(() => {
        const processDecks = async () => {
            const updated = await Promise.all(
                decks.map(async (fc) => {
                    const image = isValidURL(fc.image) ? fc.image : fc.image ? await fetchSignedUrl(fc.image) : null;
                    return { ...fc, image };
                })
            );
            setDecks(updated);
        };

        processDecks();
    }, [decks]);

    const fetchSignedUrl = async (fileKey: string): Promise<any> => {
        try {
            const res = await FlashcardService.getFile({ fileKey });
            if (res?.data?.status == 200) {
                return res?.data?.fileUrl;
            }
        } catch (err) {
            console.error("Error fetching signed URL:", err);
            return '';
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchUsers(1, searchQuery);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, []);

    const handlePageChange = (page: number) => {
        fetchUsers(page, searchQuery);
    };

    return (
        <>
            <PageMeta
                title="VIYUME - VIYUME Dashboard"
                description="User Management"
            />
            <div className="space-y-6">
                <ComponentCard title="">
                    <div className="mb-4 relative">
                        <Input
                            type="text"
                            placeholder="Search decks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onEnter={(value) => fetchUsers(1, value)}
                            className="pl-10"
                        />
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                    {loading ? (
                        <div className="animate-pulse">
                            <div className="space-y-4">
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} className="flex items-center space-x-4 py-3">
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 text-center p-4">{error}</div>
                    ) : (
                        <DeckTable
                            decks={decks}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            isLoading={loading}
                        />
                    )}
                </ComponentCard>
            </div>
        </>
    );
}
